import { Layers3 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { MONTHS } from "@/lib/format";

const ROWS = [
  { key: "occupancy", label: "Target occupancy %", hint: "e.g. 85", basis: "occupancy_growth_pct" },
  { key: "adr", label: "Target ADR (IDR)", hint: "e.g. 1500000", basis: "adr_growth_pct" },
  { key: "covers", label: "F&B covers / guests", hint: "e.g. 900", basis: "covers_growth_pct" },
  { key: "avg_check", label: "F&B average check (IDR)", hint: "e.g. 150000", basis: "fnb_price_growth_pct" },
];

export const BottomUpPanel = ({ a, set, basis, lastMonth = 0 }) => {
  const bu = a.bottom_up || {};
  const arr = (k) => (Array.isArray(bu[k]) && bu[k].length === 12 ? bu[k] : Array(12).fill(null));
  const setCell = (k, i, v) => set("bottom_up", { ...bu, [k]: arr(k).map((x, j) => (j === i ? (v === "" ? null : Number(v)) : x)) });
  const fillFrom = (k, source) => set("bottom_up", { ...bu, [k]: MONTHS.map((_, i) => (i < lastMonth ? null : source[i] ?? null)) });
  const ref = (row) => {
    const it = basis?.items?.[row.basis];
    if (!it) return null;
    const vals = (it.fy || []).map((v) => (v === null || v === undefined ? null : row.key === "occupancy" && Math.abs(v) <= 1.5 ? Math.round(v * 10000) / 100 : Math.round(v * 100) / 100));
    return vals;
  };
  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6" data-testid="bottom-up-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900 inline-flex items-center gap-2"><Layers3 size={16} className="text-emerald-700" /> Bottom-up drivers</h3>
          <p className="text-xs text-slate-500 mt-1">Rooms = available rooms × occupancy × ADR · F&B = covers × average check. Fill the open months; blank = keep the run-rate forecast for that month. Other departments follow run-rate × seasonal index.</p>
        </div>
        <label className="text-xs text-slate-600 flex items-center gap-2">Rooms available (keys)
          <Input type="number" value={bu.rooms_available ?? ""} onChange={(e) => set("bottom_up", { ...bu, rooms_available: e.target.value === "" ? null : Number(e.target.value) })} placeholder="auto from P&L" className="h-8 w-28 font-mono text-xs" data-testid="bottom-up-rooms-available" />
        </label>
      </div>
      <div className="mt-4 overflow-auto">
        <table className="min-w-full text-[11px]">
          <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500"><th className="py-1 pr-2 text-left">Driver</th>{MONTHS.map((m, i) => <th key={m} className={`py-1 px-1 text-right ${i < lastMonth ? "text-slate-300" : ""}`}>{m}</th>)}<th className="py-1 pl-2" /></tr></thead>
          <tbody>
            {ROWS.map((row) => {
              const r = ref(row);
              return (
                <tr key={row.key} className="border-t border-slate-100" data-testid={`bottom-up-row-${row.key}`}>
                  <td className="py-1 pr-2 font-medium text-slate-700 whitespace-nowrap">{row.label}{r && <span className="block text-[10px] font-normal text-slate-400">forecast now: {r.filter((v, i) => i >= lastMonth && v !== null).slice(0, 1).map((v) => v.toLocaleString("en-US"))}</span>}</td>
                  {MONTHS.map((_, i) => (
                    <td key={i} className="py-1 px-0.5">
                      <Input type="number" disabled={i < lastMonth} value={arr(row.key)[i] ?? ""} placeholder={r && i >= lastMonth && r[i] !== null ? String(Math.round(r[i])) : row.hint} onChange={(e) => setCell(row.key, i, e.target.value)} data-testid={`bottom-up-${row.key}-${i + 1}`} className="h-7 w-20 px-1 font-mono text-[10px] text-right" />
                    </td>
                  ))}
                  <td className="py-1 pl-2 whitespace-nowrap">{r && <button type="button" onClick={() => fillFrom(row.key, r)} className="text-[10px] text-emerald-700 hover:underline" data-testid={`bottom-up-fill-${row.key}`}>use current forecast</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
